// The explorer's "All files" list and navigation across changed files in it.
import type { Category } from './categorize';

export interface AllFilesFilter {
  /** Every known path: the repository tree at the head plus the PR's changed files. */
  paths: Iterable<string>;
  changed: Set<string>;
  /** Changed files that pass the changed-file filters (category, search, unreviewed, file filter). */
  changedShown: Set<string>;
  /** The file filter (type chips and path pattern) for unchanged files; none when omitted. */
  pass?: (path: string) => boolean;
  /** Lowercased search text ('' for none). */
  q: string;
  category: Category | 'all';
  categoryOf: (path: string) => Category;
  /**
   * Search match (DiffModel.matches): the path, or the source of changed
   * files and of files already opened. Other files' contents are not
   * fetched for search.
   */
  matches: (path: string, q: string) => boolean;
}

/** Paths the "All files" explorer shows. */
export function filterAll(f: AllFilesFilter): string[] {
  const out: string[] = [];
  for (const p of f.paths) {
    if (f.changed.has(p)) {
      if (f.changedShown.has(p)) out.push(p);
      continue;
    }
    if (f.pass && !f.pass(p)) continue;
    if (f.category !== 'all' && f.categoryOf(p) !== f.category) continue;
    if (f.q && !f.matches(p, f.q)) continue;
    out.push(p);
  }
  return out;
}

/**
 * Changed files after (`delta` > 0) or before the current file, nearest
 * first, in explorer order: what n/p and ]c/[c step through. `visible` is
 * the explorer's order, which may include unchanged files; `navOrder` the
 * changed files among them. From an unchanged file, navigation continues
 * with the changed files around its place in `visible`.
 */
export function changedAround(current: string | null, delta: number, navOrder: string[], visible: string[], changed: Set<string>): string[] {
  if (!current) return delta > 0 ? navOrder : [];
  const i = navOrder.indexOf(current);
  if (i >= 0) return delta > 0 ? navOrder.slice(i + 1) : navOrder.slice(0, i).reverse();
  const j = visible.indexOf(current);
  if (j < 0) return delta > 0 ? navOrder : [...navOrder].reverse();
  const seq = delta > 0 ? visible.slice(j + 1) : visible.slice(0, j).reverse();
  return seq.filter((p) => changed.has(p));
}

/**
 * The changed files n/p try after running off the end: from the first
 * changed file (delta > 0) or the last, up to and including the current
 * one, so a single changed file wraps to itself. For a current file that is
 * not a changed one, every changed file.
 */
export function wrapAround(current: string | null, delta: number, navOrder: string[]): string[] {
  const dir = delta > 0 ? navOrder : [...navOrder].reverse();
  const i = current ? dir.indexOf(current) : -1;
  return i < 0 ? dir : dir.slice(0, i + 1);
}

/**
 * Lines reviewed over the changed files the file filter keeps (all of
 * them when `pass` is omitted): the explorer total, header meter and rail.
 */
export function reviewProgress(
  paths: Iterable<string>,
  progress: (path: string) => { total: number; done: number },
  pass?: (path: string) => boolean,
): { total: number; done: number; filtered: boolean } {
  let total = 0;
  let done = 0;
  for (const p of paths) {
    if (pass && !pass(p)) continue;
    const s = progress(p);
    total += s.total;
    done += s.done;
  }
  return { total, done, filtered: !!pass };
}

/** A count as it is shown: with thousands separators. */
export const count = (n: number): string => n.toLocaleString('en-US');

/** The review counts of the Files view, each over what it names. */
export interface ScopedProgress {
  /** The comparison shown: what the file list, each file's count and n / p go by. */
  shown: { done: number; total: number; filtered: boolean };
  /** Its name ("e9433f2 → head") when it is another comparison than the pull request's own (base → head); else null. */
  comparison: string | null;
  /**
   * Beside another comparison, the pull request's own count (base → head,
   * under the same file filter): `exact` false while some of its files are
   * still being counted, null before any is. Not read for base → head.
   */
  whole: { done: number; total: number; exact: boolean } | null;
}

/**
 * The file list's review summary. Base → head is one figure, the whole
 * pull request's. Another comparison shows its own count first, under its
 * name, and the whole pull request's second, so neither is taken for the
 * other. A line is reviewed, in either, when a review mark covers it,
 * whichever view the mark was made in; the totals are the lines each
 * comparison changes. `spoken` says all of it in one sentence per count.
 */
export function progressSummary(p: ScopedProgress): { scope: string; figure: string; whole: string | null; spoken: string } {
  const { done, total, filtered } = p.shown;
  const figure = `${count(done)} / ${count(total)} lines${filtered ? '' : ' reviewed'}`;
  const said = `${count(done)} of ${count(total)} lines reviewed`;
  if (p.comparison === null) {
    return { scope: 'Whole pull request · base → head', figure, whole: null, spoken: `Whole pull request, base to head: ${said}${filtered ? ', counting only the files the filter keeps' : ''}.` };
  }
  const w = p.whole;
  const whole = !w ? 'Whole pull request: counting…' : `Whole pull request: ${count(w.done)} / ${count(w.total)}${w.exact ? '' : ' so far'}`;
  const wholeSaid = !w ? 'Whole pull request: still counting.' : `Whole pull request: ${count(w.done)} of ${count(w.total)} lines reviewed${w.exact ? '' : ' so far, still counting'}.`;
  return {
    scope: `This comparison · ${p.comparison}`,
    figure,
    whole,
    spoken: `This comparison, ${p.comparison.replace(/\s*→\s*/g, ' to ')}: ${said}. ${wholeSaid}${filtered ? ' Both count only the files the filter keeps.' : ''}`,
  };
}

export interface ChangedFilter {
  category: Category | 'all';
  /** Lowercased search text ('' for none). */
  q: string;
  matches: (path: string, q: string) => boolean;
  /** "Unreviewed": hide files whose blocks are all reviewed (`reviewed`), except the open file. */
  unreviewedOnly: boolean;
  reviewed: (path: string) => boolean;
  current: string | null;
  /** The path pattern of the file filter. */
  path: (path: string) => boolean;
  /** The type chips of the file filter. */
  type: (path: string) => boolean;
}

/**
 * The changed files the explorer lists: every filter at once. `base`
 * leaves out only the type chips (what the chips count); `shown` applies
 * them too.
 */
export function filterChanged<E extends { path: string; category: Category }>(entries: E[], f: ChangedFilter): { base: E[]; shown: E[] } {
  const base = entries.filter((e) => {
    if (!f.path(e.path)) return false;
    if (f.category !== 'all' && e.category !== f.category) return false;
    if (f.q && !f.matches(e.path, f.q)) return false;
    if (f.unreviewedOnly && e.path !== f.current && f.reviewed(e.path)) return false;
    return true;
  });
  return { base, shown: base.filter((e) => f.type(e.path)) };
}

/**
 * The lines a folder's changed files add and remove, summed over the files
 * counted so far (`counts` answers null until a file's diff is computed);
 * `complete` is false while any file is still uncounted. Null when none is
 * counted yet.
 */
export function subtreeDiff(
  paths: Iterable<string>,
  counts: (path: string) => { added: number; removed: number } | null,
): { added: number; removed: number; complete: boolean } | null {
  let added = 0;
  let removed = 0;
  let counted = 0;
  let uncounted = 0;
  for (const p of paths) {
    const c = counts(p);
    if (!c) {
      uncounted++;
      continue;
    }
    added += c.added;
    removed += c.removed;
    counted++;
  }
  return counted ? { added, removed, complete: uncounted === 0 } : null;
}

/** What decides the file the Files view opens on a comparison. */
export interface Opening {
  /** The file the URL names (`?file=`). */
  requested: string | null;
  /** The file already open. */
  current: string | null;
  /** The file last open on this pull request. */
  remembered: string | null;
  /** The comparison's changed files. */
  changed: Set<string>;
  /** True for a file the view can open (changed, or in the repository tree). */
  openable: (path: string) => boolean;
  /** The first changed file in the explorer's order, null when none. */
  first: string | null;
}

/**
 * The file to open: the one the URL names, else the one already open,
 * when they can be opened; else the remembered file when this comparison
 * changes it; else the first changed file. A link to a commit or a range
 * that leaves the remembered file alone thus opens on a change.
 */
export function openingFile(o: Opening): string | null {
  if (o.requested && o.openable(o.requested)) return o.requested;
  if (o.current && o.openable(o.current)) return o.current;
  if (o.remembered && o.changed.has(o.remembered)) return o.remembered;
  return o.first;
}
